import { useSearchParams } from "react-router-dom";
import { Inbox, Loader2, Mail, Paperclip, PencilLine, RefreshCw, Search, X } from "lucide-react";
import { useState } from "react";
import { useInboundEmail, useInboundEmails, usePollMailbox } from "../api/aronMailApi";
import type { InboundEmail } from "../types";
import { AronMailDetail } from "../components/AronMailDetail";
import { AronMailStatusBadge } from "../components/AronMailStatusBadge";
import { formatDate, formatDateTime } from "@/utils/formatDate";

/** Dzisiejsze maile godziną, wczorajsze i starsze datą — pełny stempel w dymku. */
function shortStamp(s: string): string {
  const d = new Date(s);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })
    : formatDate(d);
}

function EmailRow({
  email,
  selected,
  onSelect,
}: {
  email: InboundEmail;
  selected: boolean;
  onSelect: () => void;
}) {
  const waiting = email.pending_writes_count > 0;
  return (
    <button
      type="button"
      onClick={onSelect}
      title={formatDateTime(email.received_at)}
      className={`flex w-full items-start gap-2 border-b border-l-[3px] border-gray-100 px-2.5 py-2 text-left transition-colors dark:border-b-gray-800 ${
        selected
          ? "border-l-red-600 bg-red-50 dark:bg-red-950/20"
          : "border-l-transparent hover:bg-gray-100 dark:hover:bg-gray-800/60"
      }`}
    >
      <AronMailStatusBadge status={email.status} label={email.status_display} compact />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-xs font-semibold text-gray-900 dark:text-gray-100">
            {email.from_name || email.from_email}
          </span>
          <span className="shrink-0 font-mono text-[10px] text-gray-400">
            {shortStamp(email.received_at)}
          </span>
        </span>
        <span className="mt-0.5 flex items-center gap-1.5">
          <span className="min-w-0 flex-1 truncate text-xs text-gray-500 dark:text-gray-400">
            {email.subject || "(bez tematu)"}
          </span>
          {email.has_attachments && (
            <Paperclip size={11} className="shrink-0 text-gray-400" />
          )}
          {waiting && (
            <span
              title={`${email.pending_writes_count} ${
                email.pending_writes_count === 1
                  ? "propozycja zapisu czeka"
                  : "propozycje zapisu czekają"
              } na zatwierdzenie`}
              className="inline-flex shrink-0 items-center gap-0.5 bg-red-600 px-1 font-mono text-[10px] font-semibold text-white"
            >
              <PencilLine size={9} /> {email.pending_writes_count}
            </span>
          )}
        </span>
      </span>
    </button>
  );
}

/**
 * "Poczta ARON" — inbound emails on aron@elektim.pl. Everyone with ARON access
 * sees their own emails; the CEO sees all (including unmatched senders). The
 * write-approval gate is reused from the ARON chat (owner) or runs through the
 * CEO on-behalf endpoints.
 */
export function AronMailPage() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("e");
  const [search, setSearch] = useState("");
  const emails = useInboundEmails(search ? { search } : undefined);
  const detail = useInboundEmail(selectedId);
  const poll = usePollMailbox();
  const refreshing = emails.isFetching || poll.isPending;
  const rows = emails.data ?? [];
  const waitingCount = rows.filter((e) => e.pending_writes_count > 0).length;

  const select = (id: string | null) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set("e", id);
        else next.delete("e");
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="flex h-full min-h-0">
      {/* ── Email list ───────────────────────────────────────────────────── */}
      <div className="flex w-80 shrink-0 flex-col border-r border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-950">
        <div className="border-b border-gray-200 px-2.5 py-2 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Mail size={14} className="shrink-0 text-red-600" />
            <h1 className="min-w-0 flex-1 truncate font-mono text-xs font-bold tracking-[0.12em] text-gray-900 uppercase dark:text-gray-100">
              Poczta ARON
            </h1>
            {waitingCount > 0 && (
              <span
                title={`${waitingCount} wiadomości z propozycjami zapisu do zatwierdzenia`}
                className="inline-flex items-center gap-0.5 bg-red-600 px-1 font-mono text-[10px] font-semibold text-white"
              >
                <PencilLine size={9} /> {waitingCount}
              </span>
            )}
            <button
              type="button"
              title="Odśwież — sprawdź skrzynkę aron@elektim.pl teraz"
              disabled={refreshing}
              onClick={() => {
                // Force a mailbox poll on the backend AND refresh what we have.
                poll.mutate();
                void emails.refetch();
                if (selectedId) void detail.refetch();
              }}
              className="shrink-0 p-1 text-gray-400 transition-colors hover:text-red-600 disabled:opacity-50"
            >
              <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
            </button>
          </div>
          <div className="relative mt-1.5">
            <Search size={12} className="absolute top-1.5 left-1.5 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Szukaj: nadawca, temat…"
              className="w-full border border-gray-300 bg-white py-1 pr-6 pl-6 text-xs focus:border-red-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                title="Wyczyść"
                className="absolute top-1.5 right-1.5 text-gray-400 hover:text-red-600"
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {emails.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 size={18} className="animate-spin text-gray-400" />
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-10 text-gray-400">
              <Inbox size={22} />
              <p className="text-xs">
                {search ? "Nic nie pasuje do wyszukiwania" : "Brak wiadomości"}
              </p>
            </div>
          ) : (
            rows.map((email) => (
              <EmailRow
                key={email.id}
                email={email}
                selected={email.id === selectedId}
                onSelect={() => select(email.id)}
              />
            ))
          )}
        </div>
      </div>

      {/* ── Detail pane ──────────────────────────────────────────────────── */}
      <div className="min-w-0 flex-1 bg-white dark:bg-gray-900">
        {!selectedId ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-400">
            <Mail size={28} />
            <p className="text-sm">Wybierz wiadomość z listy</p>
            <p className="max-w-xs text-center text-xs">
              Każdy mail wysłany na aron@elektim.pl trafia tutaj — z odpowiedzią
              ARON-a i propozycjami zapisu do zatwierdzenia.
            </p>
          </div>
        ) : detail.isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 size={22} className="animate-spin text-gray-400" />
          </div>
        ) : detail.data ? (
          <AronMailDetail email={detail.data} />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">
            Nie znaleziono wiadomości.
          </div>
        )}
      </div>
    </div>
  );
}
